import type { Provider } from '@angular/core';

import { Dialog } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import type { ErrorHandlerConfig, ErrorInfo } from './error.types';

import { ErrorPageComponent } from './error-page.component';
import { createErrorInfo, ERROR_STORAGE_KEY, ErrorType } from './error.types';

/**
 * 全局错误处理服务
 * 实现Angular的ErrorHandler接口，捕获所有Angular应用内的错误
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  sentry?: Sentry.SentryErrorHandler;
  private dialogSrv = inject(Dialog);
  private config: ErrorHandlerConfig = {
    enableConsoleLog: true,
    enableReporting: false,
    errorFilter: (error: any) => {
      if (error?.constructor === Error || error?.constructor === TypeError) {
        return true;
      }
      if (error instanceof HttpErrorResponse) {
        return false;
      }
      if (error instanceof Error) {
        if (error.message.includes('No provider found')) {
          return true;
        }
      }
      return false;
    },
  };

  /**
   * 设置错误处理配置
   */
  setConfig(config: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...config };
    if (!isDevMode()) {
      this.sentry = new Sentry.SentryErrorHandler(this.config.sentryOptions);
    }
  }

  /**
   * 处理错误的主要方法
   */
  handleError(error: unknown): void {
    this.sentry?.handleError(error);
    try {
      // 控制台输出
      if (this.config.enableConsoleLog) {
        console.error('🚨 Global Angular Error:', error);
      }
      // 应用错误过滤器
      if (this.config.errorFilter && !this.config.errorFilter(error)) {
        return;
      }

      // 创建错误信息
      const errorInfo = createErrorInfo(error, ErrorType.ANGULAR, {
        originalError: error,
        source: 'ErrorHandler',
      });

      // 控制台输出
      if (this.config.enableConsoleLog) {
        console.error('📋 Error Info:', errorInfo);
      }

      // 存储错误信息
      this.storeErrorInfo(errorInfo);

      // 上报错误（如果启用）
      if (this.config.enableReporting) {
        this.reportError(errorInfo);
      }

      // 显示错误（弹窗或页面）
      this.showError(errorInfo);
    } catch (handlerError) {
      // 错误处理器本身出错时的兜底处理
      console.error('❌ Error in GlobalErrorHandler:', handlerError);
      console.error('🔄 Original error:', error);
      this.fallbackErrorHandling(error);
    }
  }

  /**
   * 存储错误信息到sessionStorage
   */
  private storeErrorInfo(errorInfo: ErrorInfo): void {
    try {
      sessionStorage.setItem(ERROR_STORAGE_KEY, JSON.stringify(errorInfo));
    } catch (storageError) {
      console.warn('⚠️ Failed to store error info:', storageError);
    }
  }

  /**
   * 上报错误到服务器
   */
  private async reportError(errorInfo: ErrorInfo): Promise<void> {
    if (!this.config.reportingEndpoint) {
      return;
    }

    try {
      await fetch(this.config.reportingEndpoint, {
        body: JSON.stringify(errorInfo),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });
    } catch (reportError) {
      console.warn('⚠️ Failed to report error:', reportError);
    }
  }

  /**
   * 显示错误信息（弹窗或页面）
   */
  private showError(errorInfo: ErrorInfo): void {
    try {
      this.dialogSrv.open(ErrorPageComponent, {
        autoFocus: '_nnnnn_item',
        height: '95vh',
        width: '100%',
      });
    } catch (displayError) {
      console.error('❌ Failed to show error:', displayError);
      this.fallbackErrorHandling(displayError);
    }
  }

  /**
   * 兜底错误处理
   */
  private fallbackErrorHandling(error: any): void {
    try {
      // 创建简单的错误信息
      const simpleErrorInfo = {
        message: error?.message || 'Critical error occurred',
        timestamp: new Date().toISOString(),
        type: ErrorType.UNKNOWN,
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      sessionStorage.setItem(ERROR_STORAGE_KEY, JSON.stringify(simpleErrorInfo));
    } catch (fallbackError) {
      // 最后的兜底：显示浏览器原生错误
      console.error('💥 Critical error in fallback handling:', fallbackError);
      alert(`应用发生严重错误，请刷新页面重试。\n错误信息: ${error?.message || 'Unknown error'}`);
    }
  }
}

/**
 * 提供GlobalErrorHandler的工厂函数
 */
export function provideGlobalErrorHandler(config?: Partial<ErrorHandlerConfig>): Provider[] {
  const res: Provider[] = [
    {
      provide: ErrorHandler,
      useFactory: () => {
        const handler = new GlobalErrorHandler();
        if (config) {
          handler.setConfig(config);
        }
        return handler;
      },
    },
  ];
  if (!isDevMode()) {
    res.push({
      provide: Sentry.TraceService,
      deps: [Router],
    });
  }
  return res;
}
